// What Settings → AI shows, worked out from the state the API sends: no React here, so it is tested on its own.
import { AI_KEY_PROVIDERS, type AiKeyDto, type AiKeyProvider, type AiKeyStatus, type AiKeysStatusDto } from "@imagora/contracts";
import type { BadgeTone } from "../../components/Badge";

export const KEY_TONE: Record<AiKeyStatus, BadgeTone> = { ok: "success", invalid: "danger", no_credit: "warning", unverified: "neutral" };

/** The providers the owner can still add, in the order the service tries them. */
export function providersToAdd(keys: ReadonlyArray<Pick<AiKeyDto, "provider">>): AiKeyProvider[] {
  return AI_KEY_PROVIDERS.filter((provider) => !keys.some((key) => key.provider === provider));
}

/** A host's workspace runs on the host's AI: there is nothing to add or choose there. */
export function showsKeys(status: Pick<AiKeysStatusDto, "reason">): boolean {
  return status.reason !== "host";
}

/** Why a key stopped working, as a key under `keys.problem.*`. Null while the key works. */
export function keyProblem(key: Pick<AiKeyDto, "status" | "lastErrorCode">): "rejected" | "no_credit" | "google_free_tier" | "denied" | "unreachable" | null {
  if (key.status === "ok") return null;
  if (key.lastErrorCode === "google_free_tier") return "google_free_tier";
  // Reached, and refused its test call's model only: the key is still tried.
  if (key.lastErrorCode === "http_403") return "denied";
  if (key.status === "no_credit") return "no_credit";
  if (key.status === "invalid") return "rejected";
  return "unreachable";
}

export type AiKeyBannerCode = NonNullable<AiKeysStatusDto["blocked"]>;

/** The line above every page while AI work is stopped for want of a working key. */
export function bannerFor(status: Pick<AiKeysStatusDto, "blocked" | "source">): AiKeyBannerCode | null {
  return status.source === "tenant_key" ? status.blocked : null;
}

/** What each kind of AI work runs on, for the summary card. `none` where no key can do it yet. */
export function capabilityRows(status: Pick<AiKeysStatusDto, "source" | "capabilities">): Array<{ id: "text" | "images" | "imageEdit"; provider: AiKeyProvider | "none" }> | null {
  if (status.source !== "tenant_key") return null;
  return (["text", "images", "imageEdit"] as const).map((id) => ({ id, provider: status.capabilities[id] ?? "none" }));
}

/** Where each provider hands out keys. Not translated: these are the providers' own pages. */
export const PROVIDER_CONSOLE: Record<AiKeyProvider, string> = {
  anthropic: "https://console.anthropic.com/settings/keys",
  google: "https://aistudio.google.com/app/apikey",
  openai: "https://platform.openai.com/api-keys",
};

/** What a refused AI call needed, as a key under `keys.capability.*`, from the `capability` the API puts in the details. */
export function refusedCapability(details: unknown): "text" | "images" | "imageEdit" | null {
  const capability = (details as { capability?: unknown } | null | undefined)?.capability;
  if (capability === "text" || capability === "vision") return "text";
  if (capability === "image") return "images";
  if (capability === "image_edit") return "imageEdit";
  return null;
}

const usable = (key: Pick<AiKeyDto, "status">) => key.status === "ok" || key.status === "unverified";

/** „Cheia mea” needs a key the provider takes: a refused or empty one would stop all the AI at once. */
export function canChooseOwn(keys: ReadonlyArray<Pick<AiKeyDto, "status">>): boolean {
  return keys.some(usable);
}

/**
 * What the workspace's working keys would leave refused on „Cheia mea”, as keys under `keys.capability.*`: the same rule as the
 * service's (Anthropic writes only text, only Google edits pictures). Nothing is moved to the plan's AI to fill a gap.
 */
export function ownKeyGaps(keys: ReadonlyArray<Pick<AiKeyDto, "provider" | "status">>): Array<"text" | "images" | "imageEdit"> {
  const has = (provider: AiKeyProvider) => keys.some((key) => key.provider === provider && usable(key));
  const gaps: Array<"text" | "images" | "imageEdit"> = [];
  if (!AI_KEY_PROVIDERS.some(has)) gaps.push("text");
  if (!has("google") && !has("openai")) gaps.push("images");
  if (!has("google")) gaps.push("imageEdit");
  return gaps;
}
